import React from 'react';
import StatCard, { StatCardRow } from '../StatCard';
import { DetailCard, DetailLabel, ExternalTextLink, MUTED_TEXT } from '../ballot-initiatives/detail/detailAtoms';
import { formatDate } from '../../utils/dateUtils';
import type { FederalFinanceData, FederalFinanceTotals } from './types';

interface FederalCampaignFinanceProps {
  finance: FederalFinanceData;
  /** "U.S. House campaign" — names what the money is for. */
  campaignLabel?: string | null;
}

/** "—" for a figure FEC hasn't reported (or we haven't imported), never "$0". */
export function fecMoney(n: number | null): string {
  return n === null ? '—' : '$' + Math.round(n).toLocaleString('en-US');
}

export const FEC_TILES: Array<{ key: keyof FederalFinanceTotals; label: string; variant?: 'success' | 'info' | 'warning' }> = [
  { key: 'receipts', label: 'Total receipts', variant: 'success' },
  { key: 'individual_contributions', label: 'Individual contributions' },
  { key: 'disbursements', label: 'Disbursements' },
  { key: 'cash_on_hand', label: 'Cash on hand', variant: 'info' },
  { key: 'debts_owed', label: 'Debts owed', variant: 'warning' },
];

/**
 * Campaign finance for a federal candidacy, from the FEC (OpenFEC candidate
 * cycle totals). Every tile is always shown so the FEC fields are laid out
 * before the first import; the committee and candidate link out to fec.gov —
 * the committee card and finance explorer are TRACER's.
 */
const FederalCampaignFinance: React.FC<FederalCampaignFinanceProps> = ({ finance, campaignLabel }) => {
  const through = finance.coverage_end_date ? formatDate(finance.coverage_end_date) : null;
  return (
    <DetailCard id="person-campaign-finance" title="Campaign finance" aside={<span className={MUTED_TEXT}>Source: FEC</span>}>
      <StatCardRow minTileWidth="10rem">
        {FEC_TILES.map(({ key, label, variant }) => {
          const value = finance.totals[key];
          // No color on a figure that isn't there, nor a warning on zero debt.
          const shown = value === null || (key === 'debts_owed' && value <= 0) ? 'default' : variant;
          return <StatCard key={key} label={label} value={fecMoney(value)} variant={shown} />;
        })}
      </StatCardRow>

      <div className="mt-4 border-t border-border-light pt-3 dark:border-border-dark">
        <DetailLabel className={`mb-1 text-[11px] tracking-[0.12em] ${MUTED_TEXT}`}>Committee</DetailLabel>
        {finance.committee
          ? (
            <ExternalTextLink href={finance.committee.fec_url} className="text-sm font-semibold">
              {finance.committee.name || finance.committee.committee_id}
            </ExternalTextLink>
          )
          : <p className="text-sm text-text-secondary dark:text-text-dark-secondary">—</p>}
        <p className={`mt-1 text-xs ${MUTED_TEXT}`}>
          {[
            campaignLabel,
            finance.cycle ? `${finance.cycle - 1}–${finance.cycle} cycle` : null,
            through ? `Reports through ${through}` : 'No FEC reports imported yet',
          ].filter(Boolean).join(' · ')}
        </p>
      </div>

      {finance.fec_url && (
        <ExternalTextLink href={finance.fec_url} className="mt-4 text-sm font-semibold">
          This candidate on FEC.gov
        </ExternalTextLink>
      )}
    </DetailCard>
  );
};

export default FederalCampaignFinance;
